import { Editor, Node, NodeEntry, Path, Point, Range, Text } from 'slate';
import { HEADING_PLUGIN_ID, HeadingLevel } from '..';
import { PARAGRAPH_PLUGIN_ID } from '../../paragraph';
import { HeadingKeywords } from 'generated/graphql';

/**
 * Under normal circumstances, heading-like nodes are upgraded to actual heading nodes.
 * This covers the case where you have a non-heading node that looks like a heading.
 *
 * Ex: "LIVER: Liver is normal" may be a text node, but should be treated as a heading
 */
export const getMaybeHeadingFormatted = (n: Node): string => {
  const pattern = /[A-Za-z\s]+:/;
  const string = Node.string(n);
  if (pattern.test(string)) {
    return string.split(':')[0].trim().replace(/\s+/g, ' ').toLowerCase();
  }
  return '';
};

/**
 * This returns the next node (from a location or from the top of the document)
 * that is either a heading node or a heading-like node.
 */
export const getNextHeadingOrHeadingLikeNode = ({
  editor,
  location,
  level = HeadingLevel.H1,
  filterFn,
}: {
  editor: Editor;
  location?: Range | Path | Point;
  level?: number;
  filterFn?: (n: Node) => boolean;
}): NodeEntry | null => {
  /**
   * Search for a heading-like node.
   * This could be a paragraph node with text that looks like a heading
   * (e.g. "LIVER: Liver is normal")
   * If a heading-like node (paragraph) is found, return the corresponding
   * heading-like text node within it
   */
  if (editor == null || editor.children.length === 0) {
    return null;
  }

  const at = location ?? Editor.start(editor, []);

  const nextHeadingLikeNode = Editor.next(editor, {
    at,
    match: (n: Node) =>
      n.type === PARAGRAPH_PLUGIN_ID &&
      n.shouldForceInline !== true &&
      getMaybeHeadingFormatted(n) !== '' &&
      (filterFn != null ? filterFn(n) : true),
  });
  if (nextHeadingLikeNode != null) {
    const headingText = Editor.nodes(editor, {
      at: nextHeadingLikeNode[1],
      match: (n: Node) =>
        Text.isText(n) &&
        getMaybeHeadingFormatted(n) !== '' &&
        (filterFn != null ? filterFn(n) : true),
    }).next();
    if (headingText.done === false) {
      return headingText.value;
    }
  }

  /**
   * If there is no heading-like node that matches, but there is a
   * proper heading node that matches instead, return it
   */
  const nextHeading = Editor.next(editor, {
    at,
    match: (n: Node) =>
      n.type === HEADING_PLUGIN_ID &&
      typeof n.level === 'number' &&
      level <= n.level &&
      (filterFn != null ? filterFn(n) : true),
  });
  if (nextHeading != null) {
    return nextHeading;
  }

  /**
   * If there are no non-empty heading-like nodes, return the next reasonable
   * node (either the next block paragraph node or last node for the given location)
   */
  return (
    Editor.next(editor, {
      at,
      match: (n: Node) => n.type === PARAGRAPH_PLUGIN_ID,
    }) ?? null
  );
};

export const getComparisonHeading = (
  editor: Editor,
  comparisonHeadingKeywords: HeadingKeywords['comparison']
): NodeEntry | undefined => {
  const maybeComparisonHeading = getNextHeadingOrHeadingLikeNode({
    editor,
    filterFn: (n: Node) => comparisonHeadingKeywords.includes(getMaybeHeadingFormatted(n)),
  });
  if (maybeComparisonHeading == null) {
    // @ts-expect-error [EN-10263] - TS2322 - Type 'null' is not assignable to type 'NodeEntry | undefined'.
    return null;
  }
  const isComparisonHeading = comparisonHeadingKeywords.includes(
    getMaybeHeadingFormatted(maybeComparisonHeading[0])
  );
  if (!isComparisonHeading) {
    // @ts-expect-error [EN-10263] - TS2322 - Type 'null' is not assignable to type 'NodeEntry | undefined'.
    return null;
  }
  return maybeComparisonHeading;
};

/**
 * Given a list of sections, this function will return the range of the target section.
 * Ex: If sections = ['COMPARISON'], this function will return the range of the
 * 'COMPARISON' section.
 * Ex: If sections = ['FINDINGS', 'LIVER'], this function will return the range of the
 * 'LIVER' section.
 *
 * @param {Editor} editor
 * @param {string[]} sections
 */
export const getHeadingSectionEdgesByRegex = (
  editor: Editor,
  sections?: Array<string>
): Range | null | undefined => {
  if (sections == null || sections.length === 0) {
    return null;
  }

  let targetHeaderPoint: Path | Point = Editor.start(editor, []);
  let headingNode: NodeEntry | null = null;
  for (let secIx = 0; secIx < sections.length; secIx++) {
    const section = sections[secIx];
    const level = secIx + 1;
    const location = level > 0 && headingNode != null ? headingNode[1] : Editor.start(editor, []);
    headingNode = getNextHeadingOrHeadingLikeNode({
      editor,
      location,
      filterFn: (n: Node) => getMaybeHeadingFormatted(n) === section.toLowerCase(),
      level,
    });
    if (headingNode == null) {
      return null;
    }
    targetHeaderPoint = headingNode[1];
  }
  if (headingNode == null) return null;

  const lastHeadingNode = getNextHeadingOrHeadingLikeNode({
    editor,
    location: targetHeaderPoint,
    filterFn: (n: Node) => !!getMaybeHeadingFormatted(n),
  });

  let anchor: Point | null | undefined = null;
  let focus: Point | null | undefined = null;

  const after = Editor.after(editor, targetHeaderPoint);
  if (after != null) {
    anchor = after;
  } else {
    const end = Editor.end(editor, targetHeaderPoint);
    anchor = end;
  }

  if (lastHeadingNode == null) {
    focus = Editor.end(editor, []);
  } else {
    const before = Editor.before(editor, lastHeadingNode[1]);
    if (before != null && Point.isBefore(before, anchor)) {
      focus = Editor.end(editor, lastHeadingNode[1]);
    } else {
      focus = before;
    }
  }

  if (anchor == null || focus == null) {
    return null;
  }

  return { anchor, focus };
};
